import { create } from "zustand";
import { persist } from "zustand/middleware";

// ============================================================================
// Types
// ============================================================================

export interface User {
  id: string;
  email: string;
  name: string;
  avatarUrl?: string | null;
  isPlatformAdmin: boolean;
  createdAt?: string;
}

export interface TenantInfo {
  id: string;
  name: string;
  slug: string;
  status: string;
  role: string;
}

// Keep for backward compatibility — alias to TenantInfo
export type TenantMembership = TenantInfo;

// ============================================================================
// Store Interface
// ============================================================================

export interface AuthState {
  // State
  user: User | null;
  tenant: TenantInfo | null;
  currentCompanyId: string | null;
  currentBranchId: string | null; // null = ทุกสาขา
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  setUser: (user: User | null) => void;
  setTenant: (tenant: TenantInfo | null) => void;
  setCurrentCompany: (companyId: string) => void;
  setCurrentBranch: (branchId: string | null) => void;
  setLoading: (loading: boolean) => void;
  reset: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState = {
  user: null,
  tenant: null,
  currentCompanyId: null,
  currentBranchId: null as string | null,
  isAuthenticated: false,
  isLoading: true,
};

// ============================================================================
// Store
// ============================================================================

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      ...initialState,

      setUser: (user) =>
        set({
          user,
          isAuthenticated: user !== null,
        }),

      setTenant: (tenant) =>
        set({ tenant }),

      setCurrentCompany: (companyId) =>
        set({ currentCompanyId: companyId, currentBranchId: null }),

      setCurrentBranch: (branchId) =>
        set({ currentBranchId: branchId }),

      setLoading: (loading) =>
        set({ isLoading: loading }),

      reset: () =>
        set({
          ...initialState,
          isLoading: false,
        }),
    }),
    {
      name: "kubdee-auth",
      // Only persist company selection, not user/tenant data
      partialize: (state) => ({
        currentCompanyId: state.currentCompanyId,
        currentBranchId: state.currentBranchId,
      }),
    }
  )
);
